import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { descargar } from "@/lib/download";
import { toast } from "sonner";
import { Lock, Unlock, Camera, Download } from "lucide-react";

export const CerrarDia = ({ cont, onDone }) => {
  const [open, setOpen] = useState(false);
  const [obs, setObs] = useState("");
  const [fotos, setFotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const addFotos = async (e) => {
    const files = [...(e.target.files || [])];
    e.target.value = "";
    const b = await Promise.all(files.map((f) => new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(f); })));
    setFotos((x) => [...x, ...b]);
  };
  const cerrar = async () => {
    setBusy(true);
    try { await api.post("/app-empaque/cierre", { observaciones: obs, fotos }); toast.success("Empaque del día cerrado"); setOpen(false); setObs(""); setFotos([]); onDone(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  if (!open) return <button onClick={() => setOpen(true)} className="w-full h-14 mb-3 rounded-lg bg-[#002FA7] text-white text-lg font-black uppercase flex items-center justify-center gap-2" data-testid="emp-cerrar-dia"><Lock className="w-5 h-5" /> Cerrar empaque del día</button>;
  return (
    <div className="mb-3 rounded-lg border-2 border-[#002FA7] p-4 space-y-3" data-testid="emp-cierre-form">
      <div className="grid grid-cols-2 gap-2 text-center">
        <div className="rounded-lg bg-emerald-600 text-white p-3"><div className="text-xs font-bold uppercase">Empacados hoy</div><div className="text-3xl font-black">{cont.empacados_hoy}</div></div>
        <div className="rounded-lg bg-amber-500 text-white p-3"><div className="text-xs font-bold uppercase">Pendientes</div><div className="text-3xl font-black">{cont.pendientes}</div></div>
      </div>
      <textarea value={obs} onChange={(e) => setObs(e.target.value)} rows={2} placeholder="Observaciones (opcional)" className="w-full rounded-lg border-2 border-zinc-300 p-3 text-base" data-testid="emp-cierre-obs" />
      <label className="w-full h-12 rounded-lg border-2 border-black text-base font-black uppercase flex items-center justify-center gap-2 cursor-pointer" data-testid="emp-cierre-fotos">
        <Camera className="w-5 h-5" /> Fotos del cierre ({fotos.length})
        <input type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={addFotos} />
      </label>
      <div className="grid grid-cols-2 gap-2">
        <button onClick={() => setOpen(false)} className="h-14 rounded-lg border-2 border-zinc-300 text-base font-bold uppercase">Cancelar</button>
        <button onClick={cerrar} disabled={busy} className="h-14 rounded-lg bg-[#002FA7] text-white text-base font-black uppercase disabled:opacity-50" data-testid="emp-cierre-confirmar">{busy ? "Cerrando…" : "Confirmar cierre"}</button>
      </div>
    </div>
  );
};

const Reabrir = ({ c, onDone }) => {
  const go = async () => {
    const motivo = window.prompt("Motivo para reabrir este cierre:");
    if (!motivo || motivo.trim().length < 3) return;
    try { await api.post(`/app-empaque/cierres/${c.id}/reabrir`, { motivo }); toast.success("Cierre reabierto"); onDone(); } catch (e) { toast.error(formatApiError(e)); }
  };
  return <button onClick={go} className="h-10 px-3 rounded-lg border-2 border-red-600 text-red-700 text-sm font-bold uppercase flex items-center gap-1" data-testid={`emp-reabrir-${c.id}`}><Unlock className="w-4 h-4" /> Reabrir</button>;
};

export const Cierres = ({ onChanged }) => {
  const { user } = useAuth();
  const [cs, setCs] = useState(null);
  const load = () => api.get("/app-empaque/cierres").then(({ data }) => setCs(data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);
  if (!cs) return null;
  const dl = (c, fmt) => descargar(`/app-empaque/reporte.${fmt}?cierre_id=${c.id}`, `cierre_empaque_${c.fecha}.${fmt}`).catch((e) => toast.error(String(e.message || e)));
  return (
    <div className="space-y-2 mb-4" data-testid="emp-cierres">
      <div className="text-sm font-black uppercase tracking-widest text-zinc-500">Cierres de empaque</div>
      {cs.length === 0 && <div className="text-sm text-zinc-400">Sin cierres.</div>}
      {cs.map((c) => (
        <div key={c.id} className={`rounded-lg border-2 p-3 ${c.estado === "cerrado" ? "border-zinc-300" : "border-amber-400 bg-amber-50"}`} data-testid={`emp-cierre-${c.id}`}>
          <div className="flex justify-between gap-2"><div className="text-lg font-black">{c.fecha}</div><div className="text-xs font-bold uppercase">{c.estado === "cerrado" ? "Cerrado" : "Reabierto"}</div></div>
          <div className="text-sm">{c.fecha_hora} · {c.by} · Empacados <b>{c.total_empacado}</b> · Pendientes <b>{c.total_pendiente}</b></div>
          {c.observaciones && <div className="text-sm text-zinc-600">{c.observaciones}</div>}
          {(c.reaperturas || []).map((r, i) => <div key={i} className="text-xs text-amber-800">Reabierto por {r.by}: {r.motivo}</div>)}
          {c.fotos?.length > 0 && <div className="flex gap-1 mt-1">{c.fotos.map((f) => <a key={f} href={f} target="_blank" rel="noreferrer"><img src={f} alt="cierre" className="w-12 h-12 object-cover rounded border" /></a>)}</div>}
          <div className="flex gap-2 mt-2">
            <button onClick={() => dl(c, "xlsx")} className="h-10 px-3 rounded-lg border-2 border-zinc-300 text-sm font-bold flex items-center gap-1" data-testid={`emp-cierre-xlsx-${c.id}`}><Download className="w-4 h-4" /> Excel</button>
            <button onClick={() => dl(c, "pdf")} className="h-10 px-3 rounded-lg border-2 border-zinc-300 text-sm font-bold flex items-center gap-1" data-testid={`emp-cierre-pdf-${c.id}`}><Download className="w-4 h-4" /> PDF</button>
            {user?.role === "admin" && c.estado === "cerrado" && <Reabrir c={c} onDone={() => { load(); onChanged(); }} />}
          </div>
        </div>
      ))}
    </div>
  );
};
